<template>
  <div class="har-page">
    <!-- 返回按钮 - 参考健康档案 -->
    <button
      type="button"
      class="nav-back-btn-fixed"
      :style="{ top: pageBackBtnTop + 'px' }"
      @click="goBack"
    >
      <i class="el-icon-arrow-left" />
    </button>

    <!-- 顶区装饰背景（对齐健康档案，不占文档流） -->
    <div class="har-header-bg" aria-hidden="true">
      <img
        src="@/assets/personalInformation/headbackground.png"
        alt=""
        class="header-bg-img"
      />
    </div>

    <!-- 顶部导航：仅状态栏 + 导航行，缩小与内容间距 -->
    <header class="device-header" :style="headerStyle">
      <div class="header-nav">
        <div class="nav-side" />
        <div class="nav-title">健康评估结果</div>
        <div class="nav-side" />
      </div>
    </header>

    <div class="har-main">
      <div v-if="loading" class="har-loading">评估结果生成中...</div>
      <div v-else-if="loadError" class="har-loading">
        <p>{{ loadError }}</p>
        <button type="button" class="har-btn har-btn--primary har-btn-retry" @click="initPage">
          重试
        </button>
      </div>

      <template v-else>
        <div ref="captureRoot" class="har-capture">
          <header class="har-hero">
            <div class="har-brand">大救星</div>
            <div class="har-hero-row">
              <h2 class="har-title">健康评估结果</h2>
              <div class="har-hero-meta">
                <span class="har-meta-text">
                  风险等级：
                  <span class="har-day-status" :class="'is-' + riskLevelKey">{{ riskLevel }}</span>
                </span>
              </div>
            </div>
            <p class="har-lead">{{ pageTitle }}</p>
          </header>

          <section class="har-card">
            <div class="har-card-head">
              <h2 class="har-card-title">血压监测</h2>
            </div>
            <template v-if="bpFilled">
              <div class="har-status-line">
                <span>您目前的血压：</span>
                <span class="har-badge-danger">{{ bpResult.levelText }}</span>
              </div>
              <p class="har-bp-values">
                收缩压 <span class="c-danger">{{ bpResult.systolic }}</span>mmhg；
                舒张压 <span class="c-danger">{{ bpResult.diastolic }}</span>mmhg
              </p>
              <p v-if="bpResult.advice" class="har-advice-line">{{ bpResult.advice }}</p>
            </template>
            <p v-else class="har-advice-line">本次未填写血压，以下为理想参考与居家自测建议。</p>
            <div class="har-ideal-box">
              <div v-if="bpResult.idealText">{{ bpResult.idealText }}</div>
              <template v-else>
                <div>您的理想血压：</div>
                <div class="har-ideal-strong">
                  收缩压 &lt;{{ bpResult.idealSystolic }}mmhg；舒张压 &lt;{{ bpResult.idealDiastolic }}mmhg
                </div>
              </template>
            </div>
          </section>

          <section class="har-card">
            <div class="har-section-title">日常居家自测建议</div>
            <div class="har-suggest-box">
              <div
                v-for="(item, i) in bpHomeTips"
                :key="'bp-tip-' + i"
                class="har-suggest-row"
              >
                <span class="har-suggest-left">
                  <i class="har-dot" />
                  {{ item.condition }}
                </span>
                <span class="har-suggest-right">{{ item.action }}</span>
              </div>
            </div>
          </section>

          <section class="har-card">
            <div class="har-card-head">
              <h2 class="har-card-title">心率监测</h2>
            </div>
            <div v-if="hrFilled" class="har-hr-current">
              您目前心率：
              <span class="har-hr-value">{{ hrResult.current }}次/分钟</span>
              <span v-if="hrResult.statusName" class="har-hr-status">（{{ hrResult.statusName }}）</span>
            </div>
            <p v-else class="har-advice-line">本次未填写心率，以下为理想参考与居家自测建议。</p>
            <div class="har-ideal-box har-ideal-box--left">
              <div class="har-hr-ideal-item">
                您的理想心率范围：<span class="har-ideal-strong">{{ formatHrRange(hrResult.matchedHrMin, hrResult.matchedHrMax) }}</span>
              </div>
              <div class="har-hr-ideal-item">
                您的理想静息心率范围：<span class="har-ideal-strong">{{ formatHrRange(hrResult.matchedIdealMin, hrResult.matchedIdealMax) }}</span>
              </div>
            </div>
          </section>

          <section class="har-card">
            <div class="har-section-title">日常居家自测建议</div>
            <div class="har-suggest-box">
              <div
                v-for="(item, i) in hrHomeTips"
                :key="'hr-tip-' + i"
                class="har-suggest-row"
              >
                <span class="har-suggest-left">
                  <i class="har-dot" />
                  {{ item.condition }}
                </span>
                <span class="har-suggest-right">{{ item.action }}</span>
              </div>
            </div>
          </section>

          <section class="har-card">
            <div class="har-section-title">健康建议</div>
            <p v-if="mindset" class="har-mindset">{{ mindset }}</p>
            <div
              v-for="block in adviceBlocks"
              :key="block.title"
              class="har-advice-block"
            >
              <div class="har-advice-head">{{ block.title }}</div>
              <ul class="har-advice-list">
                <li v-for="(tip, i) in block.items" :key="block.title + i">
                  {{ tip }}
                </li>
              </ul>
            </div>
            <p v-if="disclaimer" class="har-disclaimer">{{ disclaimer }}</p>
          </section>
        </div>

        <div class="har-actions">
          <div class="har-actions-row">
            <button
              type="button"
              class="har-btn har-btn--primary"
              :disabled="savingImage"
              @click="onGenerateImage"
            >
              {{ savingImage ? '生成中...' : '生成图片' }}
            </button>
            <button type="button" class="har-btn har-btn--ghost" @click="onRetest">
              重新测评
            </button>
          </div>
        </div>

        <div class="har-known-bar">
          <button type="button" class="har-btn har-btn--primary har-btn--known" @click="onKnown">
            我知道了
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { mapState } from 'pinia'
import { useGlobalStore } from '@/stores/global'
import { getHomeRouteName } from '@/utils/uiVersion'
import {
  submitHealthEvaluation,
  getLatestHealthEvaluation,
  uploadExaminationFile
} from '@/api/activity'

/**
 * 健康评估结果页：根据 healthAssessFlow 填写数据提交评测并渲染
 */
export default {
  name: 'HealthAssessResultShowcase',
  data() {
    return {
      loading: true,
      loadError: '',
      savingImage: false,
      evaluationId: '',
      pageTitle: 'AI小星给您一份健康建议',
      riskLevel: '',
      riskLevelColor: '',
      riskLevelCode: '',
      disclaimer: '',
      mindset: '',
      bpFilled: false,
      bpResult: {
        levelText: '',
        diastolic: null,
        systolic: null,
        advice: '',
        idealDiastolic: null,
        idealSystolic: null,
        idealText: ''
      },
      bpHomeTips: [],
      hrFilled: false,
      hrResult: {
        current: null,
        statusName: '',
        matchedHrMin: null,
        matchedHrMax: null,
        matchedIdealMin: null,
        matchedIdealMax: null
      },
      hrHomeTips: [],
      adviceBlocks: []
    }
  },
  computed: {
    ...mapState(useGlobalStore, ['statusBarHeight', 'pageContentTop', 'pageBackBtnTop']),
    /**
     * 顶栏样式：对齐健康档案，仅 paddingTop=状态栏
     * @returns {{ paddingTop: string }}
     */
    headerStyle() {
      return {
        paddingTop: Number(this.statusBarHeight || 0) + 'px'
      }
    },
    /**
     * @returns {'low'|'mid'|'high'|'attention'}
     */
    riskLevelKey() {
      if (this.riskLevelCode === 'HIGH' || this.riskLevelColor === '红色') return 'high'
      if (this.riskLevelCode === 'MEDIUM_WARNING' || this.riskLevelColor === '橙色') return 'mid'
      if (this.riskLevelCode === 'LOW_ATTENTION' || this.riskLevelColor === '蓝色') return 'attention'
      if (this.riskLevelCode === 'LOW' || this.riskLevelColor === '绿色') return 'low'
      const text = String(this.riskLevel || '')
      if (text.indexOf('高') >= 0) return 'high'
      if (text.indexOf('关注') >= 0) return 'attention'
      if (text.indexOf('低') >= 0) return 'low'
      return 'mid'
    }
  },
  mounted() {
    document.title = '健康评估结果'
    this.initPage()
  },
  methods: {
    /**
     * 返回：来自健康档案则回档案，否则优先历史，无历史回档案
     * @returns {void}
     */
    goBack() {
      if (this.$route.query.from === 'healthArchive') {
        this.$router.replace({ name: 'healthArchive' }).catch(() => {})
        return
      }
      if (window.history.length > 1) {
        this.$router.go(-1)
        return
      }
      this.$router.replace({ name: 'healthArchive' }).catch(() => {})
    },
    /**
     * @param {string} msg
     * @returns {void}
     */
    toast(msg) {
      if (this.$toast) this.$toast(msg)
      else if (this.$message) this.$message.info(msg)
    },
    /**
     * 是否直接查询最新评测结果（健康档案「查看结果」等入口）
     * @returns {boolean}
     */
    shouldLoadLatestResult() {
      const mode = this.$route.query.mode
      return mode === 'latest' || mode === 'view'
    },
    /**
     * 初始化：有填写缓存则 submit，查看结果则拉最新评测
     * @returns {Promise<void>}
     */
    async initPage() {
      this.loading = true
      this.loadError = ''
      try {
        if (this.shouldLoadLatestResult()) {
          await this.loadLatest()
          return
        }
        const form = this.readFlowForm()
        if (form) {
          await this.submitForm(form)
        } else {
          await this.loadLatest()
        }
      } catch (e) {
        console.error(e)
        this.loadError = (e && e.message) || '加载评估结果失败'
      } finally {
        this.loading = false
      }
    },
    /**
     * @returns {Object|null}
     */
    readFlowForm() {
      try {
        const raw = sessionStorage.getItem('healthAssessFlowForm')
        if (!raw) return null
        const data = JSON.parse(raw)
        if (!data || !data.nickname || !data.sex || !data.birthday) return null
        return data
      } catch (e) {
        return null
      }
    },
    /**
     * @param {Object} form
     * @returns {Promise<void>}
     */
    async submitForm(form) {
      const body = {
        nickname: String(form.nickname || '').trim(),
        sex: Number(form.sex),
        height: parseInt(form.height, 10),
        weight: Number(form.weight),
        birthday: form.birthday,
        skipDisease: !!form.skipDisease,
        skipVitals: !!form.skipVitals
      }
      if (!body.skipDisease) {
        body.personalDiseases = Array.isArray(form.personalDiseases) ? form.personalDiseases : []
        body.familyHistories = Array.isArray(form.familyHistories) ? form.familyHistories : []
      }
      if (!body.skipVitals) {
        body.systolic = form.systolic
        body.diastolic = form.diastolic
        body.heartRate = form.heartRate
      }
      const res = await submitHealthEvaluation(body)
      if (!(res.code === '1000' || res.code === 1000) || !res.data) {
        throw new Error(res.msg || '提交健康评测失败')
      }
      try {
        sessionStorage.removeItem('healthAssessFlowForm')
      } catch (e) {
        /* ignore */
      }
      this.applyResult(res.data)
    },
    /**
     * 查询最新健康评测结果
     * GET /health/api/health-evaluation/v1/latest
     * @returns {Promise<void>}
     */
    async loadLatest() {
      const res = await getLatestHealthEvaluation()
      if (!(res.code === '1000' || res.code === 1000) || !res.data) {
        throw new Error(res.msg || '暂无健康评测结果')
      }
      this.applyResult(res.data)
    },
    /**
     * 将接口 data 映射到页面展示字段
     * @param {Object} data
     * @returns {void}
     */
    applyResult(data) {
      this.evaluationId = data.id || ''
      this.pageTitle = data.title || 'AI小星给您一份健康建议'
      this.riskLevel = data.riskLevelName || ''
      this.riskLevelColor = data.riskLevelColor || ''
      this.riskLevelCode = data.riskLevel || ''
      this.disclaimer = data.disclaimer || ''

      const bp = data.bloodPressure || {}
      this.bpFilled = !!bp.filled
      this.bpResult = {
        levelText: bp.gradeName || '',
        systolic: bp.systolic,
        diastolic: bp.diastolic,
        advice: bp.actionAdvice || '',
        idealSystolic: bp.idealSystolic,
        idealDiastolic: bp.idealDiastolic,
        idealText: bp.idealText || ''
      }
      this.bpHomeTips = Array.isArray(bp.homeMonitorTips) ? bp.homeMonitorTips : []

      const hr = data.heartRate || {}
      this.hrFilled = !!hr.filled
      this.hrResult = {
        current: hr.value,
        statusName: hr.statusName || '',
        matchedHrMin: hr.matchedHrMin,
        matchedHrMax: hr.matchedHrMax,
        matchedIdealMin: hr.matchedIdealMin,
        matchedIdealMax: hr.matchedIdealMax
      }
      this.hrHomeTips = Array.isArray(hr.homeMonitorTips) ? hr.homeMonitorTips : []

      const sug = data.healthSuggestions || {}
      this.mindset = sug.mindset || ''
      const blocks = []
      ;['diet', 'exercise', 'daily'].forEach((key) => {
        const block = sug[key]
        if (block && block.title) {
          blocks.push({
            title: block.title,
            items: Array.isArray(block.items) ? block.items : []
          })
        }
      })
      this.adviceBlocks = blocks
    },
    /**
     * 格式化心率范围展示
     * @param {number|string|null|undefined} min
     * @param {number|string|null|undefined} max
     * @returns {string}
     */
    formatHrRange(min, max) {
      if (min != null && max != null) return min + '～' + max + '次/分钟'
      if (min != null) return min + '次/分钟'
      if (max != null) return max + '次/分钟'
      return '--'
    },
    /**
     * @returns {Promise<Function>}
     */
    loadHtml2Canvas() {
      return new Promise((resolve, reject) => {
        if (window.html2canvas) {
          resolve(window.html2canvas)
          return
        }
        try {
          // eslint-disable-next-line global-require
          const mod = require('html2canvas')
          resolve(mod && mod.default ? mod.default : mod)
          return
        } catch (e) {
          // CDN fallback
        }
        const existed = document.querySelector('script[data-html2canvas]')
        if (existed) {
          existed.addEventListener('load', () => resolve(window.html2canvas))
          existed.addEventListener('error', reject)
          return
        }
        const script = document.createElement('script')
        script.src = 'https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js'
        script.setAttribute('data-html2canvas', '1')
        script.onload = () => resolve(window.html2canvas)
        script.onerror = () => reject(new Error('html2canvas 加载失败'))
        document.head.appendChild(script)
      })
    },
    /**
     * 将截图上传到服务器，拿到可访问 URL
     * @param {string} dataUrl
     * @returns {Promise<string>}
     */
    async uploadGeneratedImage(dataUrl) {
      const res = await uploadExaminationFile({ file: dataUrl })
      const url = res && res.data && res.data.url ? String(res.data.url).trim() : ''
      if (!url) {
        throw new Error((res && res.msg) || '图片上传失败')
      }
      return url
    },
    /**
     * App 内通过原生协议保存网络图片到相册
     * @param {string} picUrl
     * @returns {boolean}
     */
    savePictureByNativeScheme(picUrl) {
      if (!picUrl) return false
      try {
        window.location.href = 'appdjxhealth://actionSavePicture?picUrl=' + picUrl
        return true
      } catch (e) {
        console.warn('actionSavePicture 调用失败', e)
        return false
      }
    },
    /**
     * 尝试通过 JSBridge 直接保存 base64（兼容部分原生实现）
     * @param {string} dataUrl
     * @returns {boolean}
     */
    tryNativeSaveBase64(dataUrl) {
      const pureBase64 = String(dataUrl || '').replace(/^data:image\/\w+;base64,/, '')
      try {
        if (this.$env.inAndroid && typeof JSInterface !== 'undefined') {
          if (typeof JSInterface.saveBase64Image === 'function') {
            JSInterface.saveBase64Image(dataUrl)
            return true
          }
          if (typeof JSInterface.savePicture === 'function') {
            JSInterface.savePicture(dataUrl)
            return true
          }
          if (typeof JSInterface.saveImage === 'function') {
            JSInterface.saveImage(pureBase64)
            return true
          }
        }
        if (
          this.$env.inIos &&
          window.webkit &&
          window.webkit.messageHandlers
        ) {
          const handlers = window.webkit.messageHandlers
          if (handlers.saveBase64Image) {
            handlers.saveBase64Image.postMessage(dataUrl)
            return true
          }
          if (handlers.savePicture) {
            handlers.savePicture.postMessage(dataUrl)
            return true
          }
          if (handlers.saveImage) {
            handlers.saveImage.postMessage(pureBase64)
            return true
          }
        }
      } catch (e) {
        console.warn('原生 base64 保存失败', e)
      }
      return false
    },
    /**
     * 保存生成的图片到相册 / 本机
     * @param {string} dataUrl
     * @param {string} filename
     * @returns {Promise<'album'|'download'|'preview'>}
     */
    async saveImageFile(dataUrl, filename) {
      // App：上传后走 actionSavePicture 保存到相册（与私人医生二维码保存一致）
      if (this.$env && this.$env.inDjxApp) {
        const picUrl = await this.uploadGeneratedImage(dataUrl)
        if (this.savePictureByNativeScheme(picUrl)) {
          return 'album'
        }
        // 兜底：部分原生版本可能支持直接传 base64
        if (this.tryNativeSaveBase64(dataUrl)) {
          return 'album'
        }
        throw new Error('无法调用原生保存相册能力')
      }

      // 微信小程序 web-view：上传后走小程序保存相册页
      if (this.$env && this.$env.inMiniProgram) {
        const picUrl = await this.uploadGeneratedImage(dataUrl)
        if (
          typeof wx !== 'undefined' &&
          wx.miniProgram &&
          typeof wx.miniProgram.navigateTo === 'function'
        ) {
          wx.miniProgram.navigateTo({
            url: '/pages/qrAction/index?action=save&url=' + encodeURIComponent(picUrl)
          })
          return 'album'
        }
      }

      // 普通浏览器：下载 / 移动端预览长按保存
      const link = document.createElement('a')
      link.href = dataUrl
      link.download = filename
      link.style.display = 'none'
      document.body.appendChild(link)
      link.click()
      setTimeout(() => {
        if (document.body.contains(link)) document.body.removeChild(link)
      }, 500)
      const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '')
      if (isMobile) {
        try {
          const win = window.open('')
          if (win) {
            win.document.write(
              '<html><head><meta name="viewport" content="width=device-width,initial-scale=1" /><title>' +
                filename +
                '</title></head><body style="margin:0;background:#111;"><img src="' +
                dataUrl +
                '" style="width:100%;display:block;" /><p style="color:#fff;text-align:center;padding:12px;font-size:14px;">长按图片可保存到相册</p></body></html>'
            )
            win.document.close()
            return 'preview'
          }
        } catch (e) {
          /* ignore */
        }
      }
      return 'download'
    },
    /**
     * @returns {Promise<void>}
     */
    async onGenerateImage() {
      if (this.savingImage) return
      const el = this.$refs.captureRoot
      if (!el) {
        this.toast('未找到可截图内容')
        return
      }
      this.savingImage = true
      try {
        const html2canvas = await this.loadHtml2Canvas()
        const canvas = await html2canvas(el, {
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          scale: Math.min(window.devicePixelRatio || 2, 2),
          scrollX: 0,
          scrollY: 0,
          windowWidth: el.scrollWidth,
          windowHeight: el.scrollHeight
        })
        const dataUrl = canvas.toDataURL('image/png')
        const filename = '健康评估结果_' + (this.riskLevel || '结果') + '_' + Date.now() + '.png'
        const mode = await this.saveImageFile(dataUrl, filename)
        if (mode === 'album') {
          this.toast('图片已保存到相册')
        } else if (mode === 'preview') {
          this.toast('请长按图片保存到相册')
        } else {
          this.toast('图片已生成，请查看下载文件')
        }
      } catch (e) {
        console.error('生成图片失败:', e)
        this.toast((e && e.message) || '生成图片失败，请稍后重试')
      } finally {
        this.savingImage = false
      }
    },
    /**
     * 我知道了：来自健康档案则回档案页，否则回健康首页
     * @returns {void}
     */
    onKnown() {
      if (this.$route.query.from === 'healthArchive') {
        this.$router.replace({ name: 'healthArchive' }).catch(() => {})
        return
      }
      this.$router.replace({ name: getHomeRouteName() }).catch(() => {})
    },
    /**
     * @returns {void}
     */
    onRetest() {
      try {
        sessionStorage.removeItem('healthAssessFlowForm')
      } catch (e) {
        /* ignore */
      }
      const query = {}
      if (this.$route.query.from) {
        query.from = this.$route.query.from
      }
      this.$router.replace({ name: 'healthAssessFlow', query }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.har-page {
  position: relative;
  min-height: 100vh;
  background: #f2f3f5;
  overflow-x: hidden;
}

.nav-back-btn-fixed {
  position: fixed;
  left: 16px;
  width: 28px;
  height: 28px;
  padding: 0;
  background: #ffffff;
  border: none;
  border-radius: 50%;
  color: #000000;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;
}

.nav-back-btn-fixed i {
  font-size: 16px;
  font-weight: 600;
  color: #000000;
}

.nav-back-btn-fixed:active {
  transform: scale(0.95);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

/* 顶区装饰背景：对齐健康档案，不占文档流 */
.har-header-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 220px;
  z-index: 0;
  overflow: hidden;
  pointer-events: none;
}

.header-bg-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* 顶部导航：仅状态栏 + 导航行 */
.device-header {
  position: relative;
  width: 100%;
  z-index: 2;
  flex-shrink: 0;
  box-sizing: border-box;
}

.header-nav {
  position: relative;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  min-height: 44px;
  box-sizing: border-box;
}

.nav-side {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
}

.nav-title {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  margin: 0;
  max-width: calc(100% - 120px);
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;
  font-size: 18px;
  font-weight: 500;
  color: #000000;
}

.har-main {
  position: relative;
  z-index: 1;
  max-width: 480px;
  margin: 0 auto;
  /* 预留固定「我知道了」底栏高度 */
  padding: 0 16px calc(72px + env(safe-area-inset-bottom, 0px));
}
.har-loading {
  text-align: center;
  padding: 80px 16px;
  color: #5f726a;
  font-size: 14px;
}
.har-btn-retry {
  margin-top: 16px;
  width: 140px;
}
.har-capture {
  background: #ffffff;
  border-radius: 12px;
  padding: 16px 12px 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
}
.har-hero { margin-bottom: 14px; }
.har-brand {
  font-size: 12px;
  font-weight: 600;
  color: #00a865;
  letter-spacing: 0.06em;
  margin-bottom: 8px;
}
.har-hero-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}
.har-title {
  margin: 0;
  font-size: 26px;
  font-weight: 700;
  color: #12231c;
}
.har-hero-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.har-meta-text {
  font-size: 13px;
  color: #5f726a;
  background: rgba(255, 255, 255, 0.72);
  border-radius: 999px;
  padding: 4px 10px;
  line-height: 1.4;
}
.har-day-status {
  font-size: 14px;
  font-weight: 700;
}
.har-day-status.is-low { color: #00bb82; }
.har-day-status.is-attention { color: #1e88e5; }
.har-day-status.is-mid { color: #ff9800; }
.har-day-status.is-high { color: #e53935; }
.har-lead {
  margin: 8px 0 0;
  font-size: 13px;
  color: #00bb82;
  font-weight: 600;
}
.har-card {
  background: #fff;
  border-radius: 14px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 6px 18px rgba(18, 35, 28, 0.04);
}
.har-card-head { margin-bottom: 10px; }
.har-card-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #12231c;
}
.har-section-title {
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  color: #00bb82;
  margin-bottom: 12px;
}
.har-status-line {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 14px;
  color: #4a5b54;
  margin-bottom: 8px;
}
.har-badge-danger {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 999px;
  background: rgba(229, 57, 53, 0.1);
  color: #e53935;
  font-size: 13px;
  font-weight: 600;
}
.har-bp-values {
  margin: 0 0 8px;
  font-size: 14px;
  color: #4a5b54;
}
.c-danger {
  color: #e53935;
  font-weight: 700;
}
.har-advice-line {
  margin: 0 0 12px;
  font-size: 13px;
  line-height: 1.6;
  color: #12231c;
}
.har-ideal-box {
  background: rgba(0, 187, 130, 0.1);
  border-radius: 10px;
  padding: 12px;
  text-align: center;
  font-size: 13px;
  color: #3d5a50;
}
.har-ideal-box--left { text-align: left; }
.har-ideal-strong {
  margin-top: 6px;
  font-weight: 700;
  color: #0f7a56;
}
.har-hr-current {
  text-align: center;
  font-size: 14px;
  color: #4a5b54;
  margin-bottom: 12px;
}
.har-hr-value {
  color: #00bb82;
  font-size: 20px;
  font-weight: 700;
}
.har-hr-status {
  margin-left: 4px;
  color: #e53935;
  font-size: 13px;
  font-weight: 600;
}
.har-hr-ideal-item + .har-hr-ideal-item { margin-top: 10px; }
.har-hr-ideal-item .har-ideal-strong {
  font-weight: 700;
  color: #0f7a56;
}
.har-suggest-box {
  background: #f7f3ea;
  border-radius: 10px;
  padding: 12px;
}
.har-suggest-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 0;
}
.har-suggest-row + .har-suggest-row {
  border-top: 1px dashed rgba(0, 0, 0, 0.06);
}
.har-suggest-left {
  position: relative;
  flex: 1;
  padding-left: 14px;
  font-size: 13px;
  line-height: 1.55;
  color: #4a5b54;
}
.har-dot {
  position: absolute;
  left: 0;
  top: 7px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #1e88e5;
}
.har-suggest-right {
  flex-shrink: 0;
  max-width: 46%;
  text-align: right;
  font-size: 12px;
  line-height: 1.55;
  color: #e53935;
  font-weight: 600;
}
.har-mindset {
  margin: 0 0 12px;
  font-size: 13px;
  line-height: 1.65;
  color: #12231c;
}
.har-advice-block + .har-advice-block { margin-top: 14px; }
.har-advice-head {
  font-size: 14px;
  font-weight: 700;
  color: #1e88e5;
  margin-bottom: 8px;
}
.har-advice-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.har-advice-list li {
  position: relative;
  padding-left: 14px;
  margin-bottom: 8px;
  font-size: 13px;
  line-height: 1.65;
  color: #4a5b54;
}
.har-advice-list li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 8px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #1e88e5;
}
.har-disclaimer {
  margin: 16px 0 0;
  font-size: 11px;
  line-height: 1.5;
  color: #9aa3a0;
}
.har-actions {
  margin-top: 8px;
}
.har-actions-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.har-known-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 100;
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
  background: #fff;
  box-shadow: 0 -4px 16px rgba(18, 35, 28, 0.06);
  box-sizing: border-box;
}
.har-btn {
  height: 44px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
}
.har-btn--primary {
  border: none;
  background: linear-gradient(180deg, #1ad08f 0%, #00bb82 100%);
  color: #fff;
}
.har-btn--primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
.har-btn--ghost {
  border: 1px solid #cfd8d4;
  background: #fff;
  color: #12231c;
}
.har-btn--known {
  width: 100%;
  max-width: 480px;
  display: block;
  margin: 0 auto;
}
.har-btn:active { opacity: 0.92; }
</style>



// WEBPACK FOOTER //
// src/views/healthAssessResultShowcase/index.vue